import React, { useRef, useState, useEffect } from "react";
import { Form, Button, Card, Alert } from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { Link, useHistory } from "react-router-dom";
import { FaGoogle } from "react-icons/fa";
import styled from 'styled-components';
import { getAuthErrorMessage } from "../../util/authErrors";

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 100px auto 20px;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin-top: 80px;
    padding: 0 16px;
  }
`;

const StyledCard = styled(Card)`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const GoogleButton = styled.button`
  background: white;
  border: 2px solid #4285f4;
  color: #4285f4;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export default function Login() {
  const emailRef = useRef();
  const passwordRef = useRef();
  const { login, currentUser, signInWithGoogleProvider } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (currentUser) {
      history.push("/user");
    }
  }, [currentUser, history]);

  async function handleSubmit(e) {
    e.preventDefault();
    
    try {
      setError("");
      setLoading(true);
      
      await login(emailRef.current.value, passwordRef.current.value);
      
      history.push("/user");
    } catch (error) {
      console.error('Error caught:', error);
      setError(getAuthErrorMessage(error, "We could not log you in. Please try again."));
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    try {
      setError("");
      setGoogleLoading(true);
      await signInWithGoogleProvider();
      history.push("/user");
    } catch (error) {
      setError(getAuthErrorMessage(error, "We could not sign you in with Google."));
      console.error("Google sign-in error:", error);
    }
    setGoogleLoading(false);
  }

  return (
    <LoginContainer>
      <StyledCard>
        <Card.Body>
          <h2 className="text-center mb-4">Log In</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group id="email">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" ref={emailRef} required />
            </Form.Group>
            <Form.Group id="password">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" ref={passwordRef} required />
            </Form.Group>
            <Button disabled={loading} className="w-100" type="submit">
              {loading ? "Logging in..." : "Log In"}
            </Button>
          </Form>
          
          <div className="text-center mt-3 mb-3">
            <span style={{ color: '#6c757d' }}>or</span>
          </div>
          
          <GoogleButton 
            disabled={googleLoading || loading} 
            onClick={handleGoogleSignIn}
          >
            <FaGoogle size={18} />
            {googleLoading ? 'Signing in...' : 'Sign in with Google'}
          </GoogleButton>
          
          <div className="w-100 text-center mt-3">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
        </Card.Body>
      </StyledCard>
      <div className="w-100 text-center mt-2">
        Need an account? <Link to="/signup">Sign Up</Link>
      </div>
    </LoginContainer>
  );
}
